/**
 * Dubai Police 999 Emergency Assistant – Main App Component
 */

import { useRef, useState, useEffect, useMemo } from "react";
import "./App.scss";

import { AgentProvider } from "./contexts/AgentContext";
import { useAgentContext } from "./contexts/AgentContext";
import { useTranscript } from "./contexts/TranscriptContext";
import { AltairAgent } from "./components/altair/AltairAgent";

import { ThemeProvider } from "./contexts/ThemeContext";
import { TranscriptProvider } from "./contexts/TranscriptContext";
import ControlTray from "./components/control-tray/ControlTray";
import SplashScreen from "./components/splash-screen/SplashScreen";
import LoginScreen, { UserData } from "./components/login/LoginScreen";
import WelcomeMessage from "./components/welcome-message/WelcomeMessage";
import { TranscriptPanel } from "./components/transcript-panel/TranscriptPanel";
import { TranscriptToggle } from "./components/transcript-toggle/TranscriptToggle";
import { AllPermissions } from "./utils/permissions";
import cn from "classnames";
import { logger } from "./utils/logger";
import { config } from "./config/runtime-config";

// ============================================================================
// Auth config
// ============================================================================
const BACKEND_URL = config.REACT_APP_BACKEND_URL;
const API_KEY = process.env.REACT_APP_GEMINI_API_KEY;

if (!BACKEND_URL && !API_KEY) {
  console.error("❌ No auth configured. Set REACT_APP_BACKEND_URL (config.js) or REACT_APP_GEMINI_API_KEY");
  throw new Error("Missing auth config");
}

const USE_EPHEMERAL_TOKENS = !!BACKEND_URL;
const VOICE_GENDER: 'male' = 'male'; // Dubai Police: male voice only

if (USE_EPHEMERAL_TOKENS) {
  logger.connection("🔒 Using EPHEMERAL TOKEN mode – Backend:", BACKEND_URL);
}

// Pre-establish WebSocket disabled — connections open on-demand to preserve API quota.
// To re-enable instant streaming, uncomment this block (requires sufficient Gemini quota):
// if (USE_EPHEMERAL_TOKENS && BACKEND_URL) {
//   import('./lib/token-service').then(({ prefetchInitialConnection }) => {
//     prefetchInitialConnection(BACKEND_URL, VOICE_GENDER);
//   }).catch(err => console.warn('Failed to pre-establish WS:', err));
// }

// Pre-warm the knowledge base search module
import('./utils/knowledge-base-search').catch(err => logger.warn('⚠️ KB module load failed:', err));

// ============================================================================
// Embedded mode
// ============================================================================
function parseEmbeddedParams(): {
  isEmbedded: boolean;
  initialAgent?: string;
  initialContext?: Record<string, any>;
  autoConnect?: boolean;
} {
  const params = new URLSearchParams(window.location.search);
  const isEmbedded = params.get('embedded') === 'true';
  const initialAgent = params.get('agent') || undefined;
  const autoConnect = params.get('autoConnect') === 'true';

  let initialContext: Record<string, any> | undefined;
  const contextParam = params.get('context');
  if (contextParam) {
    try { initialContext = JSON.parse(decodeURIComponent(contextParam)); } catch {}
  }
  if (!initialContext && (params.get('lang') || params.get('username'))) {
    initialContext = {};
    if (params.get('lang')) initialContext.language = params.get('lang');
    if (params.get('username')) initialContext.username = params.get('username');
    if (params.get('phone')) initialContext.phone = params.get('phone');
  }
  return { isEmbedded, initialAgent, initialContext, autoConnect };
}

// ============================================================================
// Main inner component
// ============================================================================
function MainAppContent({
  userData,
  permissions,
  onLogout,
  isEmbedded = false,
  initialAgent,
  initialContext,
}: {
  userData: UserData | null;
  permissions: AllPermissions | null;
  onLogout: () => void;
  isEmbedded?: boolean;
  initialAgent?: string;
  initialContext?: Record<string, any>;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoStream, setVideoStream] = useState<MediaStream | null>(null);
  const { disconnect, stopAudio, connected, volume } = useAgentContext();
  const { clearTranscript } = useTranscript();

  const prevUserDataRef = useRef(userData);
  useEffect(() => {
    if (prevUserDataRef.current && !userData) clearTranscript();
    prevUserDataRef.current = userData;
  }, [userData, clearTranscript]);

  const handleLogout = async () => {
    stopAudio();
    if (videoStream) { videoStream.getTracks().forEach(t => t.stop()); setVideoStream(null); }
    if (videoRef.current) videoRef.current.srcObject = null;
    localStorage.removeItem('dp_transcript');
    localStorage.removeItem('dp_transcript_panel_open');
    await disconnect();
    onLogout();
  };

  // Bot is "speaking" when connected and volume > threshold
  const isBotSpeaking = connected && volume > 0.01;

  return (
    <div className="streaming-console">
      <main>
        {/* Bot Avatar — round circle, centered */}
        <div className={cn("bot-avatar-wrapper", { speaking: isBotSpeaking, connected })}>
          <div className="bot-avatar-ring" />
          <div className="bot-avatar-ring bot-avatar-ring--2" />
          <div className="bot-avatar-circle">
            <img
              src="/splash_male.jpeg"
              alt="Dubai Police Emergency Assistant"
              className="bot-avatar-image"
              draggable={false}
            />
          </div>
        </div>

        <div className="main-app-area">
          <AltairAgent
            permissions={permissions}
            userData={userData ? { username: `${userData.firstName} ${userData.lastName}`, mobileNumber: userData.phone } : null}
            voiceGender={VOICE_GENDER}
            onLogout={handleLogout}
            videoStream={videoStream}
            embeddedMode={isEmbedded}
            initialAgent={initialAgent}
            initialContext={initialContext}
          />
          <video
            className={cn("stream", { hidden: !videoRef.current || !videoStream })}
            ref={videoRef}
            autoPlay
            playsInline
          />
        </div>
        <ControlTray
          videoRef={videoRef}
          supportsVideo={true}
          onVideoStreamChange={setVideoStream}
        >
          {userData && (
            <button
              className="action-button logout-button"
              onClick={handleLogout}
              title={`Logout (${userData.firstName})`}
            >
              <span className="material-symbols-outlined">logout</span>
            </button>
          )}
        </ControlTray>
      </main>
      <TranscriptToggle />
      <TranscriptPanel />
    </div>
  );
}

function MainApp(props: {
  userData: UserData | null;
  permissions: AllPermissions | null;
  onLogout: () => void;
  isEmbedded?: boolean;
  initialAgent?: string;
  initialContext?: Record<string, any>;
}) {
  return (
    <TranscriptProvider>
      <MainAppContent {...props} />
    </TranscriptProvider>
  );
}

// ============================================================================
// Root App
// ============================================================================
function App() {
  const embeddedParams = useMemo(() => parseEmbeddedParams(), []);
  const { isEmbedded, initialAgent, initialContext } = embeddedParams;

  const [sessionId] = useState(() => `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const [showSplash, setShowSplash] = useState(!isEmbedded);
  const [showLogin, setShowLogin] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [permissions, setPermissions] = useState<AllPermissions | null>(null);
  const [userData, setUserData] = useState<UserData | null>(null);

  useEffect(() => {
    const saved = localStorage.getItem('dp_user_data');
    if (saved) {
      try { setUserData(JSON.parse(saved)); } catch { localStorage.removeItem('dp_user_data'); }
    }
  }, []);

  // Embedded mode init
  useEffect(() => {
    if (isEmbedded) {
      setPermissions({ location: { granted: true }, microphone: { granted: true }, camera: { granted: true } });
      if (!userData) {
        const ctx = initialContext;
        setUserData({
          userId: 'embedded_user',
          firstName: ctx?.username?.split(' ')[0] || 'Guest',
          lastName: ctx?.username?.split(' ').slice(1).join(' ') || '',
          phone: ctx?.phone || '',
          language: ctx?.language || 'en',
        });
      }
    }
  }, [isEmbedded, initialContext, userData]);

  const handleSplashComplete = () => {
    setShowSplash(false);
    if (userData) setShowWelcome(true);
    else setShowLogin(true);
  };

  const handleLoginComplete = (loginData: UserData) => {
    setUserData(loginData);
    setShowLogin(false);
    setShowWelcome(true);
  };

  const handleWelcomeComplete = (grantedPermissions: AllPermissions) => {
    setPermissions(grantedPermissions);
    setShowWelcome(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('dp_user_data');
    setUserData(null);
    setPermissions(null);
    setShowLogin(true);
  };

  const showMainApp = (!showSplash && !showLogin && !showWelcome) || isEmbedded;

  // Use the male splash as a full-screen background image across the app.
  const appBackgroundStyle: React.CSSProperties = {
    backgroundImage: "url('/splash_male.jpeg')",
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
    backgroundColor: '#004d40',
    minHeight: '100vh',
  };

  return (
    <ThemeProvider>
      <div className="App" style={appBackgroundStyle}>
        {showSplash && <SplashScreen key="splash" onComplete={handleSplashComplete} gender={null} />}
        {showLogin && <LoginScreen onComplete={handleLoginComplete} />}
        {showWelcome && (
          <WelcomeMessage
            onComplete={handleWelcomeComplete}
            language={userData?.language || 'en'}
          />
        )}
        {showMainApp && (
          <AgentProvider
            key={sessionId}
            backendUrl={USE_EPHEMERAL_TOKENS ? BACKEND_URL : undefined}
            apiKey={USE_EPHEMERAL_TOKENS ? undefined : API_KEY}
          >
            <MainApp
              userData={userData}
              permissions={permissions}
              onLogout={handleLogout}
              isEmbedded={isEmbedded}
              initialAgent={initialAgent}
              initialContext={initialContext}
            />
          </AgentProvider>
        )}
      </div>
    </ThemeProvider>
  );
}

export default App;
